import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useInvoice,
  useInvoiceItems,
  useRecordPayment,
  useUpdateInvoice,
  useUpdateInvoiceStatus,
} from '../../services/httpServices/queries';
import { PageHelp } from '../../components/ui/PageHelp';
import { InfoHint } from '../../components/ui/InfoHint';
import { DateTimePicker } from '../../components/ui/DatePicker';
import { usePermissions } from '../../hooks/usePermissions';
import { useHospitalTimezone } from '../../hooks/useHospitalTimezone';
import {
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { joinFullName } from '../../utils/patientName';
import { billingService } from '../../services/httpServices/billingService';
import { printInvoicePdf } from '../../utils/printDocument';
import { InvoicePaymentCard } from './InvoicePaymentCard';
import {
  DiscountState,
  DiscountType,
  money,
  resolveDiscountAmount,
  round2,
  sanitizeDecimalInput,
} from './invoice-discount-utils';
import { formatQuantity } from '../../utils/servicePricing';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  BadgePercent,
  Ban,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Globe,
  Info,
  ListChecks,
  Loader2,
  Pencil,
  Printer,
  Receipt,
  RotateCcw,
  Save,
  StickyNote,
  Tag,
  User,
} from 'lucide-react';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

function patientName(invoice: any): string {
  const patient = invoice?.patient;
  const name = patient ? joinFullName(patient) : '';
  return name || invoice?.patientId?.slice(0, 8) || '—';
}

function normalizeStatus(status: unknown): string {
  return String(status ?? '').toLowerCase();
}

export function InvoiceEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const updateInvoice = useUpdateInvoice();
  const updateInvoiceStatus = useUpdateInvoiceStatus();
  const recordPayment = useRecordPayment();

  const { data: invoiceData, isLoading: invoiceLoading, error: invoiceError } = useInvoice(id);
  const { data: itemsData, isLoading: itemsLoading } = useInvoiceItems(id);
  const invoice = invoiceData?.data;
  const items = useMemo(() => itemsData?.data || [], [itemsData]);

  const [initialized, setInitialized] = useState(false);
  const [discounts, setDiscounts] = useState<Record<string, DiscountState>>({});
  const [invoiceDiscount, setInvoiceDiscount] = useState<DiscountState>({ value: '', type: 'PERCENT' });
  const [notes, setNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [updatedInvoice, setUpdatedInvoice] = useState<any>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // ── Backdating ──
  // A bill's date (printed on the PDF, used by the date filters / reports and
  // by the accrual ledger entry) may only be moved by a role holding
  // `billing:invoice:backdate`. Everyone else sees it read-only.
  const { hasPermission } = usePermissions();
  const canBackdate = hasPermission('billing:invoice:backdate');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceDateError, setInvoiceDateError] = useState<string | undefined>();
  const [paymentDate, setPaymentDate] = useState('');
  const originalInvoiceDate = useRef('');
  const invoiceDateSeededRef = useRef(false);

  const { timezone: hospitalTimezone, isReady: timezoneReady } = useHospitalTimezone();

  // Seed the date fields from the stored bill. The timezone may arrive after the
  // invoice; while the counter has not touched the field, a later zone still
  // corrects the seeded value. Once it is edited (or saved) the seed never
  // overwrites the deliberate change.
  useEffect(() => {
    if (!invoice || !timezoneReady) return;
    const local = toDatetimeLocalValueInTimeZone(new Date(invoice.createdAt), hospitalTimezone);
    if (
      !invoiceDateSeededRef.current ||
      invoiceDate === originalInvoiceDate.current
    ) {
      invoiceDateSeededRef.current = true;
      originalInvoiceDate.current = local;
      setInvoiceDate(local);
    }
  }, [invoice, timezoneReady, hospitalTimezone, invoiceDate]);

  // A new payment defaults to "now" in the hospital's zone.
  const paymentDateSeededRef = useRef(false);
  useEffect(() => {
    if (!timezoneReady || paymentDateSeededRef.current) return;
    paymentDateSeededRef.current = true;
    setPaymentDate(toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone));
  }, [timezoneReady, hospitalTimezone]);

  // Seed the form from the stored invoice and its line items once loaded.
  useEffect(() => {
    if (!invoice || itemsLoading || initialized) return;

    const next: Record<string, DiscountState> = {};
    let itemDiscountTotal = 0;
    for (const item of items) {
      next[item.id] = {
        value: Number(item.discountValue || 0) > 0 ? String(Number(item.discountValue)) : '',
        type: item.discountType === 'FLAT' ? 'FLAT' : 'PERCENT',
      };
      itemDiscountTotal += Number(item.discountAmount || 0);
    }
    setDiscounts(next);
    setNotes(invoice.notes || '');

    const percent = Number(invoice.discountPercent || 0);
    if (percent > 0) {
      setInvoiceDiscount({ value: String(percent), type: 'PERCENT' });
    } else {
      const flat = round2(Number(invoice.discount || 0) - itemDiscountTotal);
      setInvoiceDiscount({ value: flat > 0 ? String(flat) : '', type: 'FLAT' });
    }
    setInitialized(true);
  }, [invoice, items, itemsLoading, initialized]);

  const hasItems = items.length > 0;

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    let serviceDiscount = 0;

    if (hasItems) {
      for (const item of items) {
        const base = round2(Number(item.unitPrice || 0) * Number(item.quantity || 1));
        const entry = discounts[item.id];
        subtotal += base;
        tax += Number(item.tax || 0);
        if (entry) serviceDiscount += resolveDiscountAmount(base, entry.value, entry.type);
      }
      subtotal = round2(subtotal);
      tax = round2(tax);
      serviceDiscount = round2(serviceDiscount);
    } else {
      subtotal = round2(Number(invoice?.subtotal || 0));
      tax = round2(Number(invoice?.tax || 0));
    }

    const remaining = round2(subtotal + tax - serviceDiscount);
    const invoiceNum = parseFloat(invoiceDiscount.value);
    let invoiceDiscountAmount = 0;
    if (invoiceDiscount.value && Number.isFinite(invoiceNum) && invoiceNum > 0) {
      invoiceDiscountAmount =
        invoiceDiscount.type === 'PERCENT'
          ? round2((subtotal * Math.min(invoiceNum, 100)) / 100)
          : round2(invoiceNum);
    }
    const applied = round2(Math.min(invoiceDiscountAmount, Math.max(remaining, 0)));

    return {
      subtotal,
      tax,
      serviceDiscount,
      invoiceDiscount: applied,
      discount: round2(serviceDiscount + applied),
      remaining: Math.max(remaining, 0),
      total: round2(Math.max(remaining - applied, 0)),
    };
  }, [items, hasItems, discounts, invoiceDiscount, invoice]);

  // Payment already recorded against this invoice, and the balance still owing
  // after the current edits. A new payment is capped to that balance.
  const paidAmount = Number(invoice?.paidAmount || 0);
  const outstanding = round2(Math.max(totals.total - paidAmount, 0));
  const paymentValue = useMemo(() => {
    const num = parseFloat(paymentAmount);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return round2(Math.min(num, outstanding));
  }, [paymentAmount, outstanding]);
  const balanceAfterPayment = round2(Math.max(outstanding - paymentValue, 0));

  const updatePaymentAmount = (raw: string) => {
    setPaymentAmount(sanitizeDecimalInput(raw));
  };

  const updateDiscount = (itemId: string, raw: string, base: number) => {
    const sanitized = sanitizeDecimalInput(raw);
    setDiscounts((prev) => {
      const type = prev[itemId]?.type || 'PERCENT';
      let value = sanitized;
      const num = parseFloat(value);
      if (Number.isFinite(num)) {
        const max = type === 'PERCENT' ? 100 : base;
        if (num > max) value = String(max);
      }
      return { ...prev, [itemId]: { value, type } };
    });
  };

  const setDiscountType = (itemId: string, type: DiscountType, base: number) => {
    setDiscounts((prev) => {
      const current = prev[itemId] || { value: '', type: 'PERCENT' };
      let value = current.value;
      const num = parseFloat(value);
      if (Number.isFinite(num)) {
        const max = type === 'PERCENT' ? 100 : base;
        if (num > max) value = String(max);
      }
      return { ...prev, [itemId]: { value, type } };
    });
  };

  const updateInvoiceDiscount = (raw: string) => {
    const sanitized = sanitizeDecimalInput(raw);
    setInvoiceDiscount((prev) => {
      let value = sanitized;
      const num = parseFloat(value);
      if (Number.isFinite(num)) {
        const max = prev.type === 'PERCENT' ? 100 : totals.remaining;
        if (num > max) value = String(max);
      }
      return { ...prev, value };
    });
  };

  const setInvoiceDiscountType = (type: DiscountType) => {
    setInvoiceDiscount((prev) => {
      let value = prev.value;
      const num = parseFloat(value);
      if (type === 'PERCENT' && Number.isFinite(num) && num > 100) value = '100';
      return { ...prev, type, value };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setFormError(null);

    // Only resend the business date when a backdate-capable role actually moved
    // it, so a plain discount/notes edit never nudges the stored timestamp.
    const invoiceDateChanged =
      canBackdate && Boolean(invoiceDate) && invoiceDate !== originalInvoiceDate.current;
    if (canBackdate && !invoiceDate) {
      setInvoiceDateError('Invoice date & time is required');
      setFormError('Please choose the invoice date & time');
      return;
    }
    setInvoiceDateError(undefined);

    const invoiceDiscountValue = parseFloat(invoiceDiscount.value);
    const hasInvoiceDiscount =
      Boolean(invoiceDiscount.value) &&
      Number.isFinite(invoiceDiscountValue) &&
      invoiceDiscountValue > 0;

    const safePercent =
      totals.subtotal > 0
        ? Math.min(invoiceDiscountValue, round2((totals.remaining / totals.subtotal) * 100))
        : 0;

    const itemPayload = items.map((item) => {
      const entry = discounts[item.id];
      const value = parseFloat(entry?.value || '');
      return {
        id: item.id,
        discountType: entry?.type || 'PERCENT',
        discountValue: Number.isFinite(value) && value > 0 ? value : 0,
      };
    });

    try {
      const res = await updateInvoice.mutateAsync({
        id,
        data: {
          notes: notes.trim() || undefined,
          ...(hasItems ? { items: itemPayload } : {}),
          // Omitted unless a backdate-capable role moved it: the server then
          // keeps the stored business date untouched.
          ...(invoiceDateChanged
            ? { invoiceDate: datetimeLocalToISOInTimeZone(invoiceDate, hospitalTimezone) }
            : {}),
          ...(hasInvoiceDiscount
            ? invoiceDiscount.type === 'PERCENT'
              ? { discountPercent: safePercent }
              : { discount: Math.min(invoiceDiscountValue, totals.remaining) }
            : { discount: 0, discountPercent: 0 }),
        },
      });
      const updated = (res as any)?.data;
      let paidNow = Number(updated?.paidAmount ?? paidAmount);
      const dueNow = Number(updated?.dueAmount ?? outstanding);

      if (paymentValue > 0) {
        const amount = Math.min(paymentValue, dueNow || paymentValue);
        try {
          await recordPayment.mutateAsync({
            invoiceId: id,
            amount,
            paymentMode,
            referenceNumber: paymentReference.trim() || undefined,
            receivedBy: undefined,
            ...(canBackdate && paymentDate
              ? { paymentDate: datetimeLocalToISOInTimeZone(paymentDate, hospitalTimezone) }
              : {}),
          });
          paidNow = round2(paidNow + amount);
        } catch (payErr: any) {
          toast.error(
            `Invoice saved, but recording the payment failed: ${payErr?.message || 'unknown error'}`,
          );
        }
      }

      setUpdatedInvoice({
        ...(updated || invoice || {}),
        paidAmount: paidNow,
        dueAmount: round2(Math.max(Number(updated?.total ?? totals.total) - paidNow, 0)),
      });
      // The stored date is now the one on screen: a repeat save must not resend it.
      originalInvoiceDate.current = invoiceDate;
      setShowSuccess(true);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to update invoice');
    }
  };

  const handlePrintInvoice = async () => {
    const invoiceId = updatedInvoice?.id || id;
    if (!invoiceId) {
      navigate('/billing/invoices');
      return;
    }
    setPrintBusy(true);
    try {
      const url = await billingService.fetchInvoicePdfUrl(invoiceId);
      await printInvoicePdf(url);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to print invoice');
    } finally {
      setPrintBusy(false);
      setShowSuccess(false);
      navigate('/billing/invoices');
    }
  };

  const handleGoToList = () => {
    setShowSuccess(false);
    navigate('/billing/invoices');
  };

  /** Soft-cancel the invoice, or reopen a cancelled one. */
  const applyStatus = async (next: string) => {
    if (!id) return;
    setFormError(null);
    try {
      await updateInvoiceStatus.mutateAsync({ id, status: next });
      setConfirmCancel(false);
      toast.success(next === 'cancelled' ? 'Invoice cancelled' : 'Invoice reactivated');
    } catch (err: any) {
      setFormError(err?.message || 'Failed to update invoice status');
    }
  };

  if (invoiceLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (invoiceError || !invoice) {
    return (
      <div className="space-y-4">
        <Link
          to="/billing/invoices"
          className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text transition-colors"
        >
          <ArrowLeft size={16} />
          Back to invoices
        </Link>
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-xl text-danger">
          <AlertCircle size={18} />
          <span>Failed to load this invoice.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="space-y-3">
        <nav className="flex items-center gap-1.5 text-xs text-text-muted">
          <Link to="/billing/invoices" className="hover:text-primary transition-colors">
            Invoices
          </Link>
          <ChevronRight size={13} />
          <span className="text-text font-mono">{invoice.invoiceNumber}</span>
          <ChevronRight size={13} />
          <span className="text-text">Edit</span>
        </nav>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate('/billing/invoices')}
              className="p-2 rounded-lg border border-border bg-surface text-text-muted hover:text-text hover:bg-background transition-colors"
              title="Back to invoices"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Receipt className="w-6 h-6 text-primary" />
                <h1 className="text-2xl font-bold text-text">Edit Invoice</h1>
                <PageHelp path="/billing/invoices" />
              </div>
              <p className="text-sm text-text-muted mt-1">
                Adjust the discount on each service, apply an overall invoice discount and update
                notes. Totals recalculate automatically.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary font-mono">
            <Pencil size={13} />
            {invoice.invoiceNumber}
          </span>
        </div>
      </div>

      {formError && (
        <div className="flex items-start gap-2 p-3.5 bg-danger/10 border border-danger/20 rounded-xl text-sm text-danger">
          <AlertCircle size={17} className="shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
          {/* Left: form */}
          <div className="xl:col-span-2 space-y-6">
            {/* Patient & invoice */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                <User size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                  Patient &amp; Invoice
                </h2>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-border bg-background/50 p-4">
                  <p className="text-[11px] uppercase tracking-wide text-text-muted">Patient</p>
                  <p className="text-sm font-semibold text-text mt-1">{patientName(invoice)}</p>
                  {invoice.patient?.mobileNumber && (
                    <p className="text-xs text-text-muted mt-0.5">{invoice.patient.mobileNumber}</p>
                  )}
                  {invoice.patient?.patientCode && (
                    <p className="text-xs text-text-muted mt-0.5">Code: {invoice.patient.patientCode}</p>
                  )}
                </div>
                <div className="rounded-xl border border-border bg-background/50 p-4">
                  <p className="text-[11px] uppercase tracking-wide text-text-muted">Status</p>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-sm font-semibold text-text capitalize">{invoice.status}</p>
                    {normalizeStatus(invoice.status) === 'cancelled' ? (
                      <button
                        type="button"
                        onClick={() => applyStatus('issued')}
                        disabled={updateInvoiceStatus.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border text-xs font-medium text-text-muted hover:text-success hover:border-success transition-colors disabled:opacity-50"
                      >
                        {updateInvoiceStatus.isPending ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <RotateCcw size={13} />
                        )}
                        Reactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmCancel(true)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border text-xs font-medium text-text-muted hover:text-danger hover:border-danger transition-colors"
                      >
                        <Ban size={13} />
                        Cancel
                      </button>
                    )}
                  </div>

                  {confirmCancel && (
                    <div className="mt-2 p-2.5 rounded-lg bg-danger/10 border border-danger/20 space-y-2">
                      <p className="text-xs text-danger leading-relaxed">
                        Cancel this invoice? It and its payments are kept for the record, but no
                        further payments can be recorded. You can reopen it later.
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirmCancel(false)}
                          className="px-2.5 py-1 rounded-lg border border-border text-xs font-medium text-text-muted hover:text-text transition-colors"
                        >
                          Keep
                        </button>
                        <button
                          type="button"
                          onClick={() => applyStatus('cancelled')}
                          disabled={updateInvoiceStatus.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger-dark transition-colors disabled:opacity-50"
                        >
                          {updateInvoiceStatus.isPending && (
                            <Loader2 size={13} className="animate-spin" />
                          )}
                          Confirm Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-text-muted mt-0.5">
                    {new Date(invoice.createdAt).toLocaleString()}
                  </p>
                  {Number(invoice.paidAmount || 0) > 0 && (
                    <p className="text-xs text-success mt-0.5">
                      Paid: {money(Number(invoice.paidAmount || 0))}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Services */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border flex-wrap">
                <div className="flex items-center gap-2">
                  <Tag size={17} className="text-primary" />
                  <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                    Services &amp; Charges
                  </h2>
                  {itemsLoading && <Loader2 size={14} className="animate-spin text-text-muted" />}
                </div>
                <span className="text-xs text-text-muted">
                  {items.length} service{items.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="p-5">
                {itemsLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-10 gap-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Info size={20} className="text-primary" />
                    </div>
                    <p className="text-sm font-medium text-text">No line items</p>
                    <p className="text-xs text-text-muted max-w-sm">
                      This invoice has no stored service lines. You can still edit the overall
                      discount and notes below.
                    </p>
                  </div>
                ) : (
                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[680px]">
                        <thead>
                          <tr className="border-b border-border bg-background/60">
                            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wide">
                              Service
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wide">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wide">
                              Tax
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wide">
                              Discount
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wide">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {items.map((item) => {
                            const base = round2(Number(item.unitPrice || 0) * Number(item.quantity || 1));
                            const entry =
                              discounts[item.id] || { value: '', type: 'PERCENT' as DiscountType };
                            const lineDiscount = resolveDiscountAmount(base, entry.value, entry.type);
                            const lineTotal = base + Number(item.tax || 0) - lineDiscount;
                            return (
                              <tr key={item.id} className="hover:bg-background/40">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-medium text-text">{item.serviceName}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[11px] uppercase tracking-wide text-text-muted">
                                      Qty {formatQuantity(Number(item.quantity || 1), item.unitLabel)}
                                    </span>
                                    {item.service?.name && item.service.name !== item.serviceName && (
                                      <span className="text-xs text-text-muted">{item.service.name}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-text text-right align-top whitespace-nowrap">
                                  {money(base)}
                                </td>
                                <td className="px-4 py-3 text-sm text-text text-right align-top whitespace-nowrap">
                                  {money(Number(item.tax || 0))}
                                </td>
                                <td className="px-4 py-3 align-top">
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="flex items-stretch rounded-lg border border-border overflow-hidden bg-background">
                                      <button
                                        type="button"
                                        onClick={() => setDiscountType(item.id, 'PERCENT', base)}
                                        className={`px-2 py-1.5 text-xs font-semibold transition-colors ${
                                          entry.type === 'PERCENT'
                                            ? 'bg-primary text-white'
                                            : 'text-text-muted hover:text-text'
                                        }`}
                                        title="Percentage discount"
                                      >
                                        %
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setDiscountType(item.id, 'FLAT', base)}
                                        className={`px-2 py-1.5 text-xs font-semibold border-l border-border transition-colors ${
                                          entry.type === 'FLAT'
                                            ? 'bg-primary text-white'
                                            : 'text-text-muted hover:text-text'
                                        }`}
                                        title="Flat amount discount"
                                      >
                                        ৳
                                      </button>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0"
                                        value={entry.value}
                                        onChange={(e) => updateDiscount(item.id, e.target.value, base)}
                                        className="w-16 px-2 py-1.5 text-right text-sm bg-background text-text focus:outline-none focus:ring-1 focus:ring-primary"
                                      />
                                    </div>
                                    {lineDiscount > 0 && (
                                      <span className="text-[11px] font-medium text-danger">
                                        −{money(lineDiscount)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-text text-right align-top whitespace-nowrap">
                                  {money(lineTotal)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Notes */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                <StickyNote size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Notes</h2>
              </div>
              <div className="p-5">
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes that will appear on the invoice…"
                  className={`${inputClass} resize-none`}
                />
              </div>
            </section>

          </div>

          {/* Right: summary */}
          <div className="xl:col-span-1 space-y-6 xl:sticky xl:top-6">
            <section className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border bg-background/40">
                <CircleDollarSign size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Invoice Summary</h2>
              </div>
              <div className="p-5 space-y-3">
                {/* Invoice date & time — the bill's business date. */}
                <div>
                  <label className="mb-1 flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                    Invoice Date &amp; Time *
                    <InfoHint
                      label="Invoice Date & Time"
                      en="The date this bill is posted on. It is printed on the bill and drives the date filters, reports and ledger. Changing it (backdating) requires the “Invoice Date & Time (Backdate)” permission; otherwise the bill keeps its stored date."
                      bn="এই বিল যে তারিখে পোস্ট হয়েছে। বিলে এটি মুদ্রিত হয় এবং ডেট ফিল্টার, রিপোর্ট ও লেজারে ব্যবহৃত হয়। পরিবর্তন (ব্যাকডেট) করতে “Invoice Date & Time (Backdate)” পারমিশন প্রয়োজন; না থাকলে সংরক্ষিত তারিখ অপরিবর্তিত থাকে।"
                    />
                  </label>
                  <DateTimePicker
                    value={invoiceDate}
                    onChange={(value) => {
                      setInvoiceDate(value);
                      if (value) setInvoiceDateError(undefined);
                    }}
                    error={invoiceDateError}
                    timeZone={hospitalTimezone}
                    disabled={!canBackdate || normalizeStatus(invoice.status) === 'cancelled'}
                    clearable={canBackdate && normalizeStatus(invoice.status) !== 'cancelled'}
                    required
                  />
                  <p className="mt-1 flex items-center gap-1 text-[10px] leading-4 text-text-muted">
                    <Globe size={10} className="shrink-0" />
                    {canBackdate
                      ? `Times shown in ${hospitalTimezone} (Settings → General)`
                      : 'Read-only. Requires the “Invoice Date & Time (Backdate)” permission to change.'}
                  </p>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Subtotal</span>
                  <span className="text-text">{money(totals.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Tax</span>
                  <span className="text-text">{money(totals.tax)}</span>
                </div>
                {totals.serviceDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Service discounts</span>
                    <span className="text-danger font-medium">−{money(totals.serviceDiscount)}</span>
                  </div>
                )}

                <div className="border-t border-border pt-3 mt-1 space-y-2">
                  {totals.discount > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-muted">Total discount</span>
                      <span className="text-danger font-medium">−{money(totals.discount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text">Total payable</span>
                    <span className="text-xl font-bold text-primary">{money(totals.total)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Paid so far</span>
                    <span className="text-success">{money(paidAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text">Due</span>
                    <span
                      className={`text-base font-bold ${
                        outstanding > 0 ? 'text-danger' : 'text-success'
                      }`}
                    >
                      {money(outstanding)}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Invoice discount */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                <BadgePercent size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Invoice Discount</h2>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Applied</span>
                  <span
                    className={
                      totals.invoiceDiscount > 0 ? 'text-danger font-medium' : 'text-text-muted'
                    }
                  >
                    −{money(totals.invoiceDiscount)}
                  </span>
                </div>
                <div className="flex items-stretch rounded-lg border border-border overflow-hidden bg-surface">
                  <button
                    type="button"
                    onClick={() => setInvoiceDiscountType('PERCENT')}
                    className={`px-3 py-2 text-xs font-semibold transition-colors ${
                      invoiceDiscount.type === 'PERCENT'
                        ? 'bg-primary text-white'
                        : 'text-text-muted hover:text-text'
                    }`}
                    title="Percentage discount"
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvoiceDiscountType('FLAT')}
                    className={`px-3 py-2 text-xs font-semibold border-l border-border transition-colors ${
                      invoiceDiscount.type === 'FLAT'
                        ? 'bg-primary text-white'
                        : 'text-text-muted hover:text-text'
                    }`}
                    title="Flat amount discount"
                  >
                    ৳
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={invoiceDiscount.value}
                    onChange={(e) => updateInvoiceDiscount(e.target.value)}
                    className="flex-1 min-w-0 px-3 py-2 text-right text-sm bg-surface text-text focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <p className="text-[11px] text-text-muted leading-relaxed">
                  {invoiceDiscount.type === 'PERCENT'
                    ? 'Percentage applied to the subtotal, on top of service discounts.'
                    : 'Flat amount subtracted after service discounts.'}
                </p>
              </div>
            </section>

            <InvoicePaymentCard
              amount={paymentAmount}
              onAmountChange={updatePaymentAmount}
              mode={paymentMode}
              onModeChange={setPaymentMode}
              reference={paymentReference}
              onReferenceChange={setPaymentReference}
              maxAmount={outstanding}
              collecting={paymentValue}
              balanceAfter={balanceAfterPayment}
              paidSoFar={paidAmount}
              fullyPaid={outstanding <= 0}
              fullLabel="Pay full balance"
              paymentDate={canBackdate ? paymentDate : undefined}
              onPaymentDateChange={canBackdate ? setPaymentDate : undefined}
              timeZone={hospitalTimezone}
            />

            <div className="flex items-start gap-2.5 p-4 bg-primary/5 border border-primary/20 rounded-2xl">
              <Info size={16} className="text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-text-muted leading-relaxed">
                Toggle <span className="font-semibold text-text">%</span> for a percentage or{' '}
                <span className="font-semibold text-text">৳</span> for a flat amount — discount each
                service individually, then apply an overall invoice discount below.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/billing/invoices')}
                className="px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateInvoice.isPending}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateInvoice.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Success modal: print or go to the list */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-surface rounded-2xl border border-border p-6 w-full max-w-sm space-y-5 shadow-xl">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-bold text-text">Invoice Updated!</h3>
              <p className="text-sm text-text-muted">
                Invoice{' '}
                <span className="font-mono font-medium text-text">
                  {updatedInvoice?.invoiceNumber || invoice.invoiceNumber}
                </span>{' '}
                has been saved successfully.
              </p>
            </div>
            {Number(updatedInvoice?.total || 0) > 0 && (
              <div className="rounded-xl border border-border bg-background/60 p-3 space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Total</span>
                  <span className="text-text">{money(Number(updatedInvoice.total || 0))}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Paid</span>
                  <span className="text-success">{money(Number(updatedInvoice.paidAmount || 0))}</span>
                </div>
                <div className="flex items-center justify-between font-medium">
                  <span className="text-text-muted">Due</span>
                  <span
                    className={
                      Number(updatedInvoice.dueAmount || 0) > 0 ? 'text-danger' : 'text-success'
                    }
                  >
                    {money(Number(updatedInvoice.dueAmount || 0))}
                  </span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handlePrintInvoice}
                disabled={printBusy}
                className="px-4 py-2.5 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {printBusy ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                Print
              </button>
              <button
                type="button"
                onClick={handleGoToList}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <ListChecks size={16} />
                Go to List
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
